import React, { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import Avatar from './Avatar';
import Toast from './Toast';
import VideoTile from './VideoTile';
import { getFriendDisplayName } from '../utils/displayName';
import { useAudioOutputSupport } from '../hooks/useAudioOutputSupport';
import { isWindows, isMac, getBrowserName, isAndroid, isIOS } from '../utils/browser';
import * as callAudioGraph from '../utils/callAudioGraph';

// [B-2 단위 3-1a/b] 내 영상 작은 창(PIP) 크기·모양 및 네 모서리 기준 위치
const LOCAL_PIP_CLASS = 'w-[108px] h-[144px] rounded-2xl border border-white/40 shadow-2xl';

const PIP_CORNER_CLASSES = {
  tl: 'top-[calc(env(safe-area-inset-top,0px)+64px)] left-[calc(env(safe-area-inset-left,0px)+16px)]',
  tr: 'top-[calc(env(safe-area-inset-top,0px)+64px)] right-[calc(env(safe-area-inset-right,0px)+16px)]',
  bl: 'bottom-[calc(env(safe-area-inset-bottom,0px)+var(--pip-bottom-gap,96px)+16px)] left-[calc(env(safe-area-inset-left,0px)+16px)]',
  br: 'bottom-[calc(env(safe-area-inset-bottom,0px)+var(--pip-bottom-gap,96px)+16px)] right-[calc(env(safe-area-inset-right,0px)+16px)]',
};

// [B-2 단위 3-1c] 화면 조작 없을 때 버튼 자동 숨김 시간 (4초)
const CONTROLS_AUTO_HIDE_MS = 4000;
// [B-2 단위 3-1c 보완] 작은 창 누름 직후 탭·포커스 오인 무시 허용 창 (1초)
const PIP_PRESS_GUARD_MS = 1000;
const CALL_GUARD_TOAST_MS = 3500; // 맨 처음 화면 뒤로 가기 안내: 표시 시간 = 재표시 간격


// [B-3a 고칠 목록 ⑥] 카메라 안내에 보이는 이름에서 "(30c9:00fc)" 같은 장치 번호 괄호 제거
function stripCameraDeviceId(name) {
  if (typeof name !== 'string') return name;
  const cleaned = name.replace(/\s*\([0-9a-fA-F]{4}:[0-9a-fA-F]{4}\)\s*$/, '');
  return cleaned === '' ? name : cleaned;
}

// [B-3b 보완 (나) 커밋 2] 큰 화면 안 얼굴 창 자유 위치 드래그 훅
const FACE_PIP_WIDTH = 108;
const FACE_PIP_HEIGHT = 144;
const FACE_PIP_TOP_MARGIN = 64;
const FACE_PIP_BOTTOM_MARGIN = 16;
const FACE_PIP_SIDE_MARGIN = 16;
const DEFAULT_PIP_BOTTOM_GAP = 96;

function usePipFreeDrag({ stageRef, onTap, isMinimized = false, callState, pipSafeAreaRef }) {
  const pipRef = useRef(null);
  const [userPos, setUserPos] = useState(null);
  const [clampedPos, setClampedPos] = useState(null);

  const dragRef = useRef({
    active: false,
    pointerId: null,
    startX: 0,
    startY: 0,
    currentDx: 0,
    currentDy: 0,
    isDragging: false,
    baseLeft: 0,
    baseTop: 0,
  });

  const rafIdRef = useRef(null);
  const justDraggedRef = useRef(false);
  const dragTimeoutRef = useRef(null);

  // 울타리 계산
  const getBounds = useCallback(() => {
    let safeTop = 0;
    let safeBottom = 0;
    let safeLeft = 0;
    let safeRight = 0;

    if (pipSafeAreaRef?.current) {
      const cs = window.getComputedStyle(pipSafeAreaRef.current);
      safeTop = Math.max(0, parseFloat(cs.paddingTop) || 0);
      safeBottom = Math.max(0, parseFloat(cs.paddingBottom) || 0);
      safeLeft = Math.max(0, parseFloat(cs.paddingLeft) || 0);
      safeRight = Math.max(0, parseFloat(cs.paddingRight) || 0);
    }

    let pipBottomGap = DEFAULT_PIP_BOTTOM_GAP;
    if (stageRef?.current) {
      const gapStr = window.getComputedStyle(stageRef.current).getPropertyValue('--pip-bottom-gap');
      const parsedGap = parseFloat(gapStr);
      if (!isNaN(parsedGap) && parsedGap > 0) {
        pipBottomGap = parsedGap;
      }
    }

    const stageRect = stageRef?.current
      ? stageRef.current.getBoundingClientRect()
      : { width: window.innerWidth, height: window.innerHeight };

    const stageW = stageRect.width;
    const stageH = stageRect.height;

    const minLeft = safeLeft + FACE_PIP_SIDE_MARGIN;
    const maxLeft = Math.max(minLeft, stageW - safeRight - FACE_PIP_SIDE_MARGIN - FACE_PIP_WIDTH);
    const minTop = safeTop + FACE_PIP_TOP_MARGIN;
    const maxTop = Math.max(minTop, stageH - safeBottom - pipBottomGap - FACE_PIP_BOTTOM_MARGIN - FACE_PIP_HEIGHT);

    return { minLeft, maxLeft, minTop, maxTop };
  }, [stageRef, pipSafeAreaRef]);

  // clamp 계산 및 위치 갱신
  const updateClampedPos = useCallback(() => {
    if (!userPos) {
      setClampedPos(null);
      return;
    }
    const { minLeft, maxLeft, minTop, maxTop } = getBounds();
    const clampedX = Math.round(Math.max(minLeft, Math.min(maxLeft, userPos.left)));
    const clampedY = Math.round(Math.max(minTop, Math.min(maxTop, userPos.top)));

    setClampedPos({ left: clampedX, top: clampedY });
  }, [userPos, getBounds]);

  // userPos 변경 시 위치 갱신
  useEffect(() => {
    updateClampedPos();
  }, [updateClampedPos]);

  // 통화 종료/새 통화 시 위치 초기화
  useEffect(() => {
    if (callState !== 'connected' && userPos !== null) {
      console.info('[얼굴 창] 위치 초기화 — 계기: 통화 종료/새 통화');
      setUserPos(null);
      setClampedPos(null);
    }
  }, [callState, userPos]);

  // 화면 변화: 얼굴 창이 보이는 동안만 window resize, orientationchange를 듣고 clamp 재계산
  useEffect(() => {
    if (isMinimized) return;

    const handleResize = () => {
      updateClampedPos();
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, [isMinimized, updateClampedPos]);

  useEffect(() => {
    return () => {
      if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);
      if (dragTimeoutRef.current) clearTimeout(dragTimeoutRef.current);
    };
  }, []);

  const handlePointerDown = useCallback((e) => {
    if (isMinimized || !e.isPrimary || e.button !== 0) return;
    const el = pipRef.current;
    if (!el) return;

    try {
      el.setPointerCapture(e.pointerId);
    } catch {}

    const initialPipRect = el.getBoundingClientRect();
    const stageRect = stageRef.current
      ? stageRef.current.getBoundingClientRect()
      : { left: 0, top: 0, width: window.innerWidth, height: window.innerHeight };

    const baseLeft = initialPipRect.left - stageRect.left;
    const baseTop = initialPipRect.top - stageRect.top;

    dragRef.current = {
      active: true,
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      currentDx: 0,
      currentDy: 0,
      isDragging: false,
      baseLeft,
      baseTop,
    };
  }, [stageRef, isMinimized]);

  const handlePointerMove = useCallback((e) => {
    if (!dragRef.current.active) return;
    const dx = e.clientX - dragRef.current.startX;
    const dy = e.clientY - dragRef.current.startY;

    if (!dragRef.current.isDragging) {
      if (Math.hypot(dx, dy) <= 8) return;
      dragRef.current.isDragging = true;
      if (pipRef.current) {
        pipRef.current.style.transition = 'none';
      }
    }

    dragRef.current.currentDx = dx;
    dragRef.current.currentDy = dy;

    if (!rafIdRef.current) {
      rafIdRef.current = requestAnimationFrame(() => {
        rafIdRef.current = null;
        if (pipRef.current && dragRef.current.isDragging) {
          pipRef.current.style.transform = `translate3d(${dragRef.current.currentDx}px, ${dragRef.current.currentDy}px, 0)`;
        }
      });
    }
  }, []);

  const finishDrag = useCallback((e, isCancel = false) => {
    if (!dragRef.current.active) return;
    const isDragging = dragRef.current.isDragging;
    const pointerId = dragRef.current.pointerId;
    const baseLeft = dragRef.current.baseLeft;
    const baseTop = dragRef.current.baseTop;
    const dx = dragRef.current.currentDx;
    const dy = dragRef.current.currentDy;

    dragRef.current.active = false;
    dragRef.current.isDragging = false;

    if (rafIdRef.current) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }

    try {
      if (pointerId != null && pipRef.current?.hasPointerCapture?.(pointerId)) {
        pipRef.current.releasePointerCapture(pointerId);
      }
    } catch {}

    if (!isDragging) {
      if (!isCancel) {
        console.log('[작은창] 누르기');
        onTap?.();
      }
      return;
    }

    justDraggedRef.current = true;
    if (dragTimeoutRef.current) clearTimeout(dragTimeoutRef.current);
    dragTimeoutRef.current = setTimeout(() => {
      justDraggedRef.current = false;
      dragTimeoutRef.current = null;
    }, 250);

    if (pipRef.current) {
      pipRef.current.style.transform = '';
    }

    if (!isCancel) {
      const targetLeft = baseLeft + dx;
      const targetTop = baseTop + dy;
      const { minLeft, maxLeft, minTop, maxTop } = getBounds();
      const clampedX = Math.round(Math.max(minLeft, Math.min(maxLeft, targetLeft)));
      const clampedY = Math.round(Math.max(minTop, Math.min(maxTop, targetTop)));

      console.info(
        `[얼굴 창] 위치 이동 — x=${clampedX}, y=${clampedY} (울타리 위 ${minTop} / 아래 ${maxTop} / 좌 ${minLeft} / 우 ${maxLeft})`
      );

      setUserPos({ left: clampedX, top: clampedY });
    }
  }, [getBounds, onTap]);

  const handlePointerUp = useCallback((e) => {
    finishDrag(e, false);
  }, [finishDrag]);

  const handlePointerCancel = useCallback((e) => {
    finishDrag(e, true);
  }, [finishDrag]);

  const handleClickCapture = useCallback((e) => {
    if (justDraggedRef.current) {
      e.stopPropagation();
      e.preventDefault();
      justDraggedRef.current = false;
    }
  }, []);

  const pipPos = userPos ? (clampedPos || userPos) : null;

  const pipHandlers = {
    onPointerDown: handlePointerDown,
    onPointerMove: handlePointerMove,
    onPointerUp: handlePointerUp,
    onPointerCancel: handlePointerCancel,
    onClickCapture: handleClickCapture,
  };

  return {
    pipRef,
    pipPos,
    pipHandlers,
  };
}


// [B-3b 보완 (나) 커밋 1] 작게 보기 창 자유 위치 드래그 훅
const FREE_PIP_WIDTH = 120;
const FREE_PIP_HEIGHT = 160;
const FREE_PIP_APP_MAX_W = 480;
const FREE_PIP_SIDE_MARGIN = 12;
const FREE_PIP_TOP_MARGIN = 64;
const FREE_PIP_BOTTOM_MARGIN = 92;

function useFreeDrag({ isMinimized, onTap, callState, safeAreaRef }) {
  const freePipRef = useRef(null);
  const [userPos, setUserPos] = useState(null);
  const [clampedPos, setClampedPos] = useState(null);

  const dragRef = useRef({
    active: false,
    pointerId: null,
    startX: 0,
    startY: 0,
    currentDx: 0,
    currentDy: 0,
    isDragging: false,
    initialRect: null,
  });

  const rafIdRef = useRef(null);
  const justDraggedRef = useRef(false);
  const dragTimeoutRef = useRef(null);
  const wasPushedByClampRef = useRef(false);
  const tapPendingRef = useRef(false);

  // 울타리 바운더리 계산 함수
  const getBounds = useCallback(() => {
    if (typeof window === 'undefined') {
      return { minLeft: 12, maxLeft: 300, minTop: 64, maxTop: 600, safeAreaTop: 0, safeAreaBottom: 0, viewportHeight: 600 };
    }

    const vv = window.visualViewport;
    const screenWidth = vv ? vv.width : window.innerWidth;
    const viewportHeight = vv ? vv.height : window.innerHeight;
    const viewportOffsetTop = vv ? vv.offsetTop : 0;

    let safeAreaTop = 0;
    let safeAreaBottom = 0;
    if (safeAreaRef?.current) {
      const cs = window.getComputedStyle(safeAreaRef.current);
      safeAreaTop = Math.max(0, parseFloat(cs.paddingTop) || 0);
      safeAreaBottom = Math.max(0, parseFloat(cs.paddingBottom) || 0);
    }

    const marginX = Math.max(FREE_PIP_SIDE_MARGIN, (screenWidth - FREE_PIP_APP_MAX_W) / 2 + FREE_PIP_SIDE_MARGIN);
    const minLeft = marginX;
    const maxLeft = Math.max(minLeft, screenWidth - marginX - FREE_PIP_WIDTH);

    const minTop = viewportOffsetTop + safeAreaTop + FREE_PIP_TOP_MARGIN;
    const maxTop = Math.max(
      minTop,
      viewportOffsetTop + viewportHeight - safeAreaBottom - FREE_PIP_BOTTOM_MARGIN - FREE_PIP_HEIGHT
    );

    return { minLeft, maxLeft, minTop, maxTop, safeAreaTop, safeAreaBottom, viewportHeight };
  }, [safeAreaRef]);

  // clamp 계산 및 위치 갱신
  const updateClampedPos = useCallback(() => {
    if (!userPos) {
      setClampedPos(null);
      wasPushedByClampRef.current = false;
      return;
    }
    const { minLeft, maxLeft, minTop, maxTop } = getBounds();
    const clampedX = Math.round(Math.max(minLeft, Math.min(maxLeft, userPos.x)));
    const clampedY = Math.round(Math.max(minTop, Math.min(maxTop, userPos.y)));

    if (clampedY < userPos.y) {
      if (!wasPushedByClampRef.current) {
        wasPushedByClampRef.current = true;
        console.info('[작게 보기] 자판·화면 변화로 울타리 안으로 이동');
      }
    } else {
      wasPushedByClampRef.current = false;
    }

    setClampedPos({ x: clampedX, y: clampedY });
  }, [userPos, getBounds]);

  // userPos 변경 시 위치 갱신
  useEffect(() => {
    updateClampedPos();
  }, [updateClampedPos]);

  // 작게 보기 진입 시 1회 울타리 정보 로그 출력
  useEffect(() => {
    if (!isMinimized) return;
    const { minLeft, maxLeft, minTop, maxTop, safeAreaTop, safeAreaBottom, viewportHeight } = getBounds();
    console.info(
      `[작게 보기] 울타리 — 위 ${minTop} / 아래 ${maxTop} / 좌 ${minLeft} / 우 ${maxLeft} / 안전여백 위 ${safeAreaTop} 아래 ${safeAreaBottom} / 화면높이 ${viewportHeight}`
    );
  }, [isMinimized, getBounds]);

  // 통화 종료/새 통화 시 위치 초기화
  useEffect(() => {
    if (callState !== 'connected' && userPos !== null) {
      console.info('[작게 보기] 위치 초기화 — 계기: 통화 종료/새 통화');
      setUserPos(null);
      setClampedPos(null);
      wasPushedByClampRef.current = false;
    }
  }, [callState, userPos]);

  // viewport/visualViewport 리스너: isMinimized일 때만 등록 및 해제
  useEffect(() => {
    if (!isMinimized) return;

    const handleViewportChange = () => {
      updateClampedPos();
    };

    window.addEventListener('resize', handleViewportChange);
    window.addEventListener('orientationchange', handleViewportChange);
    const vv = window.visualViewport;
    if (vv) {
      vv.addEventListener('resize', handleViewportChange);
      vv.addEventListener('scroll', handleViewportChange);
    }

    return () => {
      window.removeEventListener('resize', handleViewportChange);
      window.removeEventListener('orientationchange', handleViewportChange);
      if (vv) {
        vv.removeEventListener('resize', handleViewportChange);
        vv.removeEventListener('scroll', handleViewportChange);
      }
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
      if (dragTimeoutRef.current) {
        clearTimeout(dragTimeoutRef.current);
        dragTimeoutRef.current = null;
      }
    };
  }, [isMinimized, updateClampedPos]);

  const handlePointerDown = useCallback((e) => {
    if (!isMinimized || !e.isPrimary || e.button !== 0) return;
    tapPendingRef.current = false;
    const el = freePipRef.current;
    if (!el) return;

    try {
      el.setPointerCapture(e.pointerId);
    } catch {}

    dragRef.current = {
      active: true,
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      currentDx: 0,
      currentDy: 0,
      isDragging: false,
      initialRect: el.getBoundingClientRect(),
    };
  }, [isMinimized]);

  const handlePointerMove = useCallback((e) => {
    if (!dragRef.current.active) return;
    const dx = e.clientX - dragRef.current.startX;
    const dy = e.clientY - dragRef.current.startY;
    const dist = Math.hypot(dx, dy);

    if (!dragRef.current.isDragging) {
      if (dist <= 8) return;
      dragRef.current.isDragging = true;
    }

    const initialRect = dragRef.current.initialRect;
    if (!initialRect) return;

    const { minLeft, maxLeft, minTop, maxTop } = getBounds();
    const targetX = initialRect.left + dx;
    const targetY = initialRect.top + dy;
    const clampedX = Math.max(minLeft, Math.min(maxLeft, targetX));
    const clampedY = Math.max(minTop, Math.min(maxTop, targetY));

    dragRef.current.currentDx = clampedX - initialRect.left;
    dragRef.current.currentDy = clampedY - initialRect.top;

    if (!rafIdRef.current) {
      rafIdRef.current = requestAnimationFrame(() => {
        rafIdRef.current = null;
        if (freePipRef.current && dragRef.current.isDragging) {
          freePipRef.current.style.transform = `translate3d(${dragRef.current.currentDx}px, ${dragRef.current.currentDy}px, 0)`;
        }
      });
    }
  }, [getBounds]);

  const finishDrag = useCallback((e, isCancel = false) => {
    if (!dragRef.current.active) return;
    const isDragging = dragRef.current.isDragging;
    const pointerId = dragRef.current.pointerId;
    const initialRect = dragRef.current.initialRect;
    const currentDx = dragRef.current.currentDx;
    const currentDy = dragRef.current.currentDy;

    dragRef.current.active = false;
    dragRef.current.isDragging = false;

    if (rafIdRef.current) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }

    try {
      if (pointerId != null && freePipRef.current?.hasPointerCapture?.(pointerId)) {
        freePipRef.current.releasePointerCapture(pointerId);
      }
    } catch {}

    if (freePipRef.current) {
      freePipRef.current.style.transform = '';
    }

    if (!isDragging) {
      if (!isCancel) {
        tapPendingRef.current = true;
      } else {
        tapPendingRef.current = false;
      }
      return;
    }

    justDraggedRef.current = true;
    if (dragTimeoutRef.current) clearTimeout(dragTimeoutRef.current);
    dragTimeoutRef.current = setTimeout(() => {
      justDraggedRef.current = false;
      dragTimeoutRef.current = null;
    }, 250);

    if (initialRect && !isCancel) {
      const finalX = Math.round(initialRect.left + currentDx);
      const finalY = Math.round(initialRect.top + currentDy);
      console.info(`[작게 보기] 위치 이동 — x=${finalX}, y=${finalY}`);
      setUserPos({ x: finalX, y: finalY });
    }
  }, []);

  const handlePointerUp = useCallback((e) => {
    finishDrag(e, false);
  }, [finishDrag]);

  const handlePointerCancel = useCallback((e) => {
    finishDrag(e, true);
  }, [finishDrag]);

  const handleClickCapture = useCallback((e) => {
    if (justDraggedRef.current) {
      e.stopPropagation();
      e.preventDefault();
      justDraggedRef.current = false;
      return;
    }
    if (tapPendingRef.current) {
      tapPendingRef.current = false;
      e.stopPropagation();
      e.preventDefault();
      onTap?.();
    }
  }, [onTap]);

  const freeHandlers = {
    onPointerDown: handlePointerDown,
    onPointerMove: handlePointerMove,
    onPointerUp: handlePointerUp,
    onPointerCancel: handlePointerCancel,
    onClickCapture: handleClickCapture,
  };

  return {
    freePipRef,
    userPos,
    clampedPos,
    freeHandlers,
  };
}

/**
 * 1:1 음성 통화 모달
 * - 발신 중: 상대 이름 + "연결 중..." + 통화 종료 버튼 (기존 확정 디자인 유지)
 * - 수신 중: 상대 이름 + 받기 / 거절 버튼 (기존 확정 디자인 유지)
 * - 통화 중: 상대 이름 (+ 상대방 음소거 알림) + 경과 시간 + 종료 버튼 + 구분선 + 기능 버튼 4개(음소거, 스피커, 화상, 통역)
 */
export default function VoiceCallModal({
  callState,
  incomingKind = 'audio',
  isAccepting = false,
  callerInfo,
  partner,
  elapsedSeconds = 0,
  remoteAudioRef,
  isMuted = false,
  peerMuted = false,
  onToggleMute,
  showToast,
  onAccept,
  onDecline,
  onEnd,
  isMicLocked = false,
  hasRecoveredOfferSdp = true,
  onReconnectAudio,
  getCurrentMicLabel,
  micChangeNotice,
  cameraOn = false,
  onStartCamera,
  onStopCamera,
  cameraNotice,
  peerVideoCapable = false,
  switchCameraFacing,
  cameraDeviceCount = 0,
  localVideoStream = null,
  remoteVideoStream = null,
  remoteVideoOn = false,
  cameraFacing = 'user',
  peerRecoveryRemainingSec = null,
  isNetworkReconnectingSelf = false,
  isNetworkReconnectingPeer = false,
  isPeerAudioUnstable = false,
  isReconnectedSuccess = false,
  isAudioConnected = false,
}) {
  const { t } = useTranslation();
  const [isSpeakerToggled, setIsSpeakerToggled] = useState(false);
  const [isGuideOpen, setIsGuideOpen] = useState(false);
  const [modalToast, setModalToast] = useState(null);
  const [modalToastDuration, setModalToastDuration] = useState(3000);
  const [modalToastKey, setModalToastKey] = useState(0);
  const toastTimerRef = useRef(null);
  const lastNoticeIdRef = useRef(0);
  const lastCameraNoticeIdRef = useRef(0);

  // [C-9] 원격 오디오 자동재생 차단(NotAllowedError) 상태 및 켜기 핸들러
  const [isAudioBlocked, setIsAudioBlocked] = useState(false);

  useEffect(() => {
    return callAudioGraph.onAudioBlockedChange((blocked) => {
      setIsAudioBlocked(blocked);
    });
  }, []);

  useEffect(() => {
    if (callState === 'idle') {
      setIsAudioBlocked(false);
      callAudioGraph.setAudioBlocked(false);
    }
  }, [callState]);

  const handleUnblockAudio = async () => {
    try {
      const ctx = callAudioGraph.getSharedAudioContext();
      if (ctx && ctx.state !== 'running') {
        await ctx.resume().catch(() => {});
      }
      if (remoteAudioRef?.current) {
        await remoteAudioRef.current.play().catch(() => {});
      }
      callAudioGraph.setAudioBlocked(false);
      setIsAudioBlocked(false);
      console.log('[소리 회복] 사용자 터치로 소리 켜기 완료');
    } catch (err) {
      console.warn('[소리 회복] 소리 켜기 실패:', err);
    }
  };

  // 통화 중 화면 표시 여부 (idle 상태에서는 렌더링 안 함)
  const isVisible = callState !== 'idle';

  // T-07 개발 스위치 및 상대 화상 지원 여부 계산
  const effectivePeerVideoCapable = (() => {
    if (import.meta.env.DEV) {
      try {
        if (typeof window !== 'undefined' && window.localStorage?.getItem('moowoo_debug_peer_video_incapable') === '1') {
          return false;
        }
      } catch {
        /* 의도적 무시: 디버그 플래그 확인 실패 환경 */
      }
    }
    return peerVideoCapable === true;
  })();

  const isVideoEnabled = true; // 2026-11-01 선장님 결정: 1:1 화상 전면 개방
  const videoMode = Boolean(isVideoEnabled && (cameraOn || remoteVideoOn));
  const hasRemoteVideo = Boolean(videoMode && remoteVideoOn && remoteVideoStream);
  const hasLocalVideo = Boolean(videoMode && cameraOn && localVideoStream);
  // [B-2 단위 3-1c] 큰 화면 자리 바꾸기(spotlight) 상태 및 계산
  const [spotlight, setSpotlight] = useState('remote');
  const effectiveSpotlight = (hasRemoteVideo && hasLocalVideo) ? spotlight : 'remote';

  // [B-3b 단위 1] 통화 화면 작게 보기(앱 안 작은 창) 상태
  const [isMinimized, setIsMinimized] = useState(false);
  const showMinimizeButton = Boolean(
    isVideoEnabled && callState === 'connected' && (hasLocalVideo || hasRemoteVideo) && !isMinimized
  );

  // [PWA전용] 네이티브 대체: 화면 방향 플러그인(Screen Orientation)으로 교체
  // [B-3c 2차] 통화 큰 화면 화면 회전 버튼 (전체 화면 + 회전 허용, 터치 기기 전용)
  const canRotate = typeof window !== 'undefined'
    && !!document.documentElement?.requestFullscreen
    && !!window.screen?.orientation?.lock
    && window.matchMedia?.('(pointer: coarse)')?.matches === true;
  const showRotateButton = showMinimizeButton && canRotate;
  const [isRotateOn, setIsRotateOn] = useState(false);

  const exitRotate = useCallback((reason) => {
    const hasFs = typeof document !== 'undefined' && Boolean(document.fullscreenElement);
    if (!isRotateOn && !hasFs) {
      return;
    }
    try {
      window.screen?.orientation?.unlock?.();
    } catch {}
    if (hasFs) {
      document.exitFullscreen().catch(() => {});
    }
    setIsRotateOn(false);
    console.log('[화면 회전] 끔 — 이유: ' + reason);
  }, [isRotateOn]);

  const handleRotateClick = useCallback(async () => {
    if (isRotateOn) {
      exitRotate('버튼');
      return;
    }

    if (typeof document === 'undefined' || !document.documentElement?.requestFullscreen) {
      console.log('[화면 회전] 실패 — 단계: 전체 화면, 오류: NotSupported');
      showToast?.(t('call.rotate_unsupported', '이 기기에서는\n화면 회전을 쓸 수 없어요'), 3500);
      return;
    }

    // 사용자 동작 조건 유지: await 전에 즉시 호출하여 promise 획득
    const fsPromise = document.documentElement.requestFullscreen();

    try {
      await fsPromise;
    } catch (e) {
      console.log('[화면 회전] 실패 — 단계: 전체 화면, 오류: ' + (e?.name || e));
      showToast?.(t('call.rotate_unsupported', '이 기기에서는\n화면 회전을 쓸 수 없어요'), 3500);
      return;
    }

    try {
      await window.screen?.orientation?.lock?.('any');
      setIsRotateOn(true);
      console.log('[화면 회전] 켬 — 전체 화면+회전 허용');
      showToast?.(t('call.rotate_guide', "폰을 가로로 눕혀 보세요.\n화면이 안 돌면 빠른 설정에서\n'자동 회전'을 켜 주세요"), 4500);
    } catch (e) {
      if (document.fullscreenElement) {
        document.exitFullscreen().catch(() => {});
      }
      console.log('[화면 회전] 실패 — 단계: 회전, 오류: ' + (e?.name || e));
      showToast?.(t('call.rotate_unsupported', '이 기기에서는\n화면 회전을 쓸 수 없어요'), 3500);
    }
  }, [isRotateOn, exitRotate, showToast, t]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      if (!document.fullscreenElement && isRotateOn) {
        try {
          window.screen?.orientation?.unlock?.();
        } catch {}
        setIsRotateOn(false);
        console.log('[화면 회전] 끔 — 이유: 전체 화면 해제(뒤로 가기·제스처 등)');
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, [isRotateOn]);

  // [B-3c] 조건 해제(작게 보기·연결 끊김·카메라 꺼짐·통화 종료) 시 회전 끄기
  useEffect(() => {
    if (isRotateOn && !showRotateButton) {
      exitRotate('조건 해제');
    }
  }, [showRotateButton, isRotateOn, exitRotate]);

  // [B-3c] 통화 화면이 닫힐 때(언마운트) 한 번만: 남은 전체 화면·회전 허용 정리
  useEffect(() => {
    return () => {
      if (typeof document !== 'undefined' && document.fullscreenElement) {
        try {
          window.screen?.orientation?.unlock?.();
        } catch {}
        document.exitFullscreen().catch(() => {});
        console.log('[화면 회전] 끔 — 이유: 통화 화면 닫힘');
      }
    };
  }, []);

  // 통화 종료 또는 비연결 상태 시 기본 배치('remote')로 복귀
  useEffect(() => {
    if (callState !== 'connected') {
      setSpotlight('remote');
    }
  }, [callState]);

  // 영상 타일 역할 계산
  const isRemotePip = Boolean(hasRemoteVideo && hasLocalVideo && effectiveSpotlight === 'local');
  const isLocalPip = Boolean(hasRemoteVideo && hasLocalVideo && effectiveSpotlight === 'remote');
  const hasBigScreenVideo = effectiveSpotlight === 'local' ? hasLocalVideo : hasRemoteVideo;

  // [B-2 단위 3-1b] 작은 창(PIP) 자석 모서리 및 드래그 훅
  const modalRootRef = useRef(null);
  const controlsRef = useRef(null);
  const stageRef = useRef(null);
  const remoteTileRef = useRef(null);
  const localTileRef = useRef(null);

  // 자리가 바뀔 때 이전 작은 창의 inline transform / transition 초기화
  const prevSpotlightRef = useRef(effectiveSpotlight);
  useLayoutEffect(() => {
    if (prevSpotlightRef.current !== effectiveSpotlight) {
      if (remoteTileRef.current) {
        remoteTileRef.current.style.transform = '';
        remoteTileRef.current.style.transition = '';
      }
      if (localTileRef.current) {
        localTileRef.current.style.transform = '';
        localTileRef.current.style.transition = '';
      }
      prevSpotlightRef.current = effectiveSpotlight;
    }
  }, [effectiveSpotlight]);

  // 3-1c 연계: 작은 창 탭 시 큰 화면과 자리 바꾸기
  const onPipTap = useCallback(() => {
    setSpotlight((prev) => {
      const next = prev === 'remote' ? 'local' : 'remote';
      console.log(`[작은창] 자리 바꾸기 — 큰 화면: ${prev === 'remote' ? '상대→나' : '나→상대'}`);
      return next;
    });
  }, []);

  const pipSafeAreaRef = useRef(null);
  const { pipRef, pipPos, pipHandlers } = usePipFreeDrag({
    stageRef,
    onTap: onPipTap,
    isMinimized,
    callState,
    pipSafeAreaRef,
  });
  const pipPressRef = useRef({ active: false, at: 0 });

  // [B-3b 보완 (나) 커밋 1] 작게 보기 창 자유 위치 드래그 연동
  const safeAreaRef = useRef(null);
  const handleTapToExpand = useCallback(() => {
    console.info('[작게 보기] 크게 — 계기: 작은 창 누름');
    setIsMinimized(false);
  }, []);

  const { freePipRef, userPos, clampedPos, freeHandlers } = useFreeDrag({
    isMinimized,
    onTap: handleTapToExpand,
    callState,
    safeAreaRef,
  });

  // [B-2 단위 3-1c] 버튼 숨김 허용 조건 및 4초 자동 숨김 제어
  const isCallNormal = Boolean(
    isAudioConnected &&
    !isNetworkReconnectingSelf &&
    !isNetworkReconnectingPeer &&
    peerRecoveryRemainingSec === null
  );
  const canHideControls = Boolean(callState === 'connected' && videoMode && hasBigScreenVideo && isCallNormal && !isMinimized);

  const [isControlsVisible, setIsControlsVisible] = useState(true);
  const isControlsVisibleRef = useRef(true);
  useEffect(() => {
    isControlsVisibleRef.current = isControlsVisible;
  }, [isControlsVisible]);

  const hideTimerRef = useRef(null);
  const clearHideTimer = useCallback(() => {
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  }, []);

  const startHideTimer = useCallback((triggerReason = '자동 4초') => {
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
    if (!canHideControls) return;
    hideTimerRef.current = setTimeout(() => {
      hideTimerRef.current = null;
      setIsControlsVisible((prev) => {
        if (prev) {
          console.log(`[통화 화면] 버튼 숨김 — 계기: ${triggerReason}`);
          return false;
        }
        return prev;
      });
    }, CONTROLS_AUTO_HIDE_MS);
  }, [canHideControls]);

  // 숨김 조건 변경 감지: 조건 미충족 시 즉시 표시 및 타이머 중단, 충족 시 4초 타이머 시작
  useEffect(() => {
    if (!canHideControls) {
      clearHideTimer();
      setIsControlsVisible((prev) => {
        if (!prev) {
          console.log('[통화 화면] 버튼 표시 — 계기: 조건 해제');
        }
        return true;
      });
    } else {
      startHideTimer('자동 4초');
    }
    return () => {
      clearHideTimer();
    };
  }, [canHideControls, clearHideTimer, startHideTimer]);

  // [B-3b 단위 1] 작게 보기 자동 복귀(크게 보기) 조건 감시
  const isOverlayCondition = Boolean(
    isNetworkReconnectingSelf ||
    isNetworkReconnectingPeer ||
    peerRecoveryRemainingSec !== null
  );

  useEffect(() => {
    if (!isMinimized) return;

    if (callState !== 'connected') {
      console.info('[작게 보기] 크게 — 계기: 통화 상태 변경');
      setIsMinimized(false);
      return;
    }

    if (!hasLocalVideo && !hasRemoteVideo) {
      console.info('[작게 보기] 크게 — 계기: 영상 없음');
      setIsMinimized(false);
      return;
    }

    if (isOverlayCondition) {
      console.info('[작게 보기] 크게 — 계기: 재연결 안내');
      setIsMinimized(false);
      return;
    }
  }, [isMinimized, callState, hasLocalVideo, hasRemoteVideo, isOverlayCondition]);

  // [PWA전용] 네이티브 대체: 안드로이드 BackButton 핸들러 및 iOS 스와이프 제스처 리스너
  // [B-3b / ⑰ 2차] 큰 화면 및 작은 창 뒤로 가기 제어용 보호 칸 관리(B안)
  const location = useLocation();
  const isRootScreen = (typeof window !== 'undefined' ? (window.history.state?.idx ?? 0) : 0) === 0;

  const ignoreNextPopRef = useRef(false);
  const ignoreNextPopTimerRef = useRef(null);
  const guardOnTopRef = useRef(typeof window !== 'undefined' ? window.history.state?.mwCallGuard === true : false);
  const lastIdxRef = useRef(typeof window !== 'undefined' ? (window.history.state?.idx ?? 0) : 0);
  const lastToastTimeRef = useRef(0);

  // C. location.key 변경 시(새 화면 라우팅 포함) 보호 칸 및 idx 상태 동기화
  useEffect(() => {
    if (typeof window !== 'undefined') {
      guardOnTopRef.current = window.history.state?.mwCallGuard === true;
      lastIdxRef.current = window.history.state?.idx ?? 0;
    }
  }, [location.key]);

  const removeGuard = useCallback((reason) => {
    if (typeof window === 'undefined') return;
    if (window.history.state?.mwCallGuard === true && !ignoreNextPopRef.current) {
      ignoreNextPopRef.current = true;
      if (ignoreNextPopTimerRef.current) {
        clearTimeout(ignoreNextPopTimerRef.current);
      }
      ignoreNextPopTimerRef.current = setTimeout(() => {
        ignoreNextPopRef.current = false;
        ignoreNextPopTimerRef.current = null;
      }, 1000);
      guardOnTopRef.current = false;
      console.info(`[뒤로 가기] 보호 칸 치움 — 계기: ${reason}`);
      window.history.back();
    }
  }, []);

  // 타이머 메모리 해제
  useEffect(() => {
    return () => {
      if (ignoreNextPopTimerRef.current) {
        clearTimeout(ignoreNextPopTimerRef.current);
        ignoreNextPopTimerRef.current = null;
      }
    };
  }, []);

  // B. 보호 칸 넣기: needGuard = isVisible && (!isMinimized || isRootScreen)
  useEffect(() => {
    const needGuard = isVisible && (!isMinimized || isRootScreen);
    if (needGuard) {
      if (typeof window !== 'undefined' && window.history.state?.mwCallGuard !== true) {
        window.history.pushState({ ...window.history.state, mwCallGuard: true }, '', window.location.href);
        guardOnTopRef.current = true;
        lastIdxRef.current = window.history.state?.idx ?? 0;
        const reason = !isMinimized ? '큰 화면' : '맨 처음 화면';
        console.info(`[뒤로 가기] 보호 칸 넣음 — 상태: ${callState}, 이유: ${reason}`);
      }
    }
  }, [isVisible, isMinimized, isRootScreen, location.key, callState]);

  // D. popstate 처리기(전체 생명주기 등록)
  useEffect(() => {
    const handlePopState = (event) => {
      const currentIdx = event.state?.idx ?? 0;
      const prevGuardOnTop = guardOnTopRef.current;

      // (1) ignoreNextPopRef가 켜져 있으면 소비하고 끝낸다(1차 그대로).
      if (ignoreNextPopRef.current) {
        ignoreNextPopRef.current = false;
        if (ignoreNextPopTimerRef.current) {
          clearTimeout(ignoreNextPopTimerRef.current);
          ignoreNextPopTimerRef.current = null;
        }
        guardOnTopRef.current = event.state?.mwCallGuard === true;
        lastIdxRef.current = currentIdx;
        return;
      }

      // (2) 도착한 칸이 보호 칸(e.state?.mwCallGuard === true)인 경우:
      if (event.state?.mwCallGuard === true) {
        if (!isVisible && currentIdx < lastIdxRef.current) {
          // 통화 중이 아니고(!isVisible) 뒤로 이동(e.state.idx < lastIdxRef)이면, 묻혀 있던 보호 칸이다.
          console.info('[뒤로 가기] 묻힌 보호 칸 정리');
          ignoreNextPopRef.current = true;
          if (ignoreNextPopTimerRef.current) {
            clearTimeout(ignoreNextPopTimerRef.current);
          }
          ignoreNextPopTimerRef.current = setTimeout(() => {
            ignoreNextPopRef.current = false;
            ignoreNextPopTimerRef.current = null;
          }, 1000);
          guardOnTopRef.current = false;
          window.history.back();
          return;
        }
        // 그 밖의 경우는 아무것도 하지 않는다.
        guardOnTopRef.current = true;
        lastIdxRef.current = currentIdx;
        return;
      }

      // (2b) 늦게 온 신호 무시: 화면 이동 뒤 이미 맨 위에 보호 칸이 들어간 상태에서 뒤늦게 도착한 일반 popstate 신호
      if (window.history.state?.mwCallGuard === true && event.state?.mwCallGuard !== true) {
        console.info('[뒤로 가기] 늦게 온 신호 무시 — 화면 이동 뒤 보호 칸 이미 있음');
        return;
      }

      // (3) 직전 맨 위가 보호 칸이었고 이번 이동으로 벗어난 경우(보호 칸이 치워진 경우):
      if (prevGuardOnTop) {
        if (!isMinimized) {
          // 큰 화면 + 화상 연결 중(1차 조건 그대로)이면 setIsMinimized(true).
          const canMinimize = Boolean(
            isVideoEnabled && callState === 'connected' && (hasLocalVideo || hasRemoteVideo)
          );
          if (canMinimize) {
            console.info('[작게 보기] 줄임 — 계기: 뒤로 가기');
            setIsMinimized(true);
            guardOnTopRef.current = false;
            lastIdxRef.current = currentIdx;
            // 그 뒤 B 규칙에 따라 맨 처음 화면이면 보호 칸이 자동으로 다시 들어간다.
          } else {
            // 큰 화면 + 그 밖의 경우: 1차처럼 보호 칸을 다시 넣는다(무시).
            window.history.pushState({ ...window.history.state, mwCallGuard: true }, '', window.location.href);
            guardOnTopRef.current = true;
            lastIdxRef.current = window.history.state?.idx ?? 0;
            console.info('[뒤로 가기] 무시 — 상태: ' + callState + ' (작게 보기 불가)');
          }
        } else {
          // 작은 창(이 경우는 맨 처음 화면): "통화 중입니다" 토스트를 띄우고(1500ms 안에 다시 오면 토스트는 생략) 보호 칸을 다시 넣는다.
          const now = Date.now();
          if (now - lastToastTimeRef.current >= CALL_GUARD_TOAST_MS) {
            lastToastTimeRef.current = now;
            if (showToast) {
              showToast(t('call.in_progress', '통화 중입니다.\n한 번 더 누르면\n통화가 끊길 수 있어요'), CALL_GUARD_TOAST_MS);
            }
          }
          window.history.pushState({ ...window.history.state, mwCallGuard: true }, '', window.location.href);
          guardOnTopRef.current = true;
          lastIdxRef.current = window.history.state?.idx ?? 0;
          console.info('[뒤로 가기] 막음 — 맨 처음 화면, 안내: 통화 중입니다');
        }
        return;
      }

      // (4) 보호 칸과 관계없는 이동(작은 창 상태에서 채팅방→목록 등 라우터 뒤로 가기)이면 아무것도 하지 않는다.
      guardOnTopRef.current = false;
      lastIdxRef.current = currentIdx;
    };

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isVisible, isMinimized, isVideoEnabled, callState, hasLocalVideo, hasRemoteVideo, showToast, t]);

  // F. 통화 종료(!isVisible): 맨 위가 보호 칸일 때만 removeGuard('통화 종료').
  useEffect(() => {
    if (!isVisible) {
      if (guardOnTopRef.current && typeof window !== 'undefined' && window.history.state?.mwCallGuard === true) {
        removeGuard('통화 종료');
      }
      setIsMinimized(false);
    }
    return () => {
      if (guardOnTopRef.current && typeof window !== 'undefined' && window.history.state?.mwCallGuard === true) {
        removeGuard('통화 종료');
      }
    };
  }, [isVisible, removeGuard]);

  // 크게 보기 복귀 시 버튼 표시 및 타이머 연동
  const prevMinimizedRef = useRef(false);
  useEffect(() => {
    if (prevMinimizedRef.current && !isMinimized) {
      console.log('[통화 화면] 버튼 표시 — 계기: 크게 보기');
      setIsControlsVisible(true);
      if (canHideControls) {
        startHideTimer('자동 4초');
      }
    }
    prevMinimizedRef.current = isMinimized;
  }, [isMinimized, canHideControls, startHideTimer]);

  // 화면 탭으로 숨김/표시 전환 (작은 창이나 버튼 클릭 시 무시)
  const handleModalClick = useCallback((e) => {
    e.stopPropagation();
    if (!canHideControls) return;

    // [B-2 단위 3-1c 보완] 직전 pointerdown이 작은 창이었으면(1000ms 이내) 화면 탭 무시
    const now = performance.now();
    if (pipPressRef.current.active) {
      const elapsed = now - pipPressRef.current.at;
      pipPressRef.current.active = false;
      if (elapsed <= PIP_PRESS_GUARD_MS) {
        console.log('[통화 화면] 화면 탭 무시 — 작은 창 누름');
        return;
      }
    }

    const target = e.target;
    if (
      pipRef.current?.contains(target) ||
      target.closest?.('button, a, input, [role="button"]')
    ) {
      return;
    }

    setIsControlsVisible((prev) => {
      const next = !prev;
      if (next) {
        console.log('[통화 화면] 버튼 표시 — 계기: 화면 탭');
        startHideTimer('자동 4초');
      } else {
        console.log('[통화 화면] 버튼 숨김 — 계기: 화면 탭');
        clearHideTimer();
      }
      return next;
    });
  }, [canHideControls, pipRef, startHideTimer, clearHideTimer]);

  // 버튼 줄 또는 작은 창에서 pointerdown 발생 시 4초 타이머 재시작 (capture)
  const handleModalPointerDownCapture = useCallback((e) => {
    if (isMinimized || !canHideControls) return;
    const target = e.target;
    const isPip = Boolean(pipRef.current?.contains(target));
    if (isPip) {
      pipPressRef.current = { active: true, at: performance.now() };
    } else {
      pipPressRef.current.active = false;
    }

    if (controlsRef.current?.contains(target) || isPip) {
      if (isControlsVisibleRef.current) {
        startHideTimer('자동 4초');
      }
    }
  }, [isMinimized, canHideControls, pipRef, startHideTimer]);

  // 마우스 이동 시 표시 및 타이머 재시작 (터치는 무시, 이미 표시 중이면 렌더 생략)
  const handleModalPointerMove = useCallback((e) => {
    if (isMinimized || e.pointerType !== 'mouse' || !canHideControls) return;
    if (!isControlsVisibleRef.current) {
      console.log('[통화 화면] 버튼 표시 — 계기: 마우스');
      setIsControlsVisible(true);
    }
    startHideTimer('자동 4초');
  }, [isMinimized, canHideControls, startHideTimer]);

  // 키보드 포커스 진입 시 표시 및 타이머 재시작
  const handleModalFocusCapture = useCallback(() => {
    if (isMinimized || !canHideControls) return;

    // [B-2 단위 3-1c 보완②] 작은 창 누름 직후(1000ms 이내) 포커스는 무시 (pipPressRef는 읽기만 하고 소비 금지)
    if (pipPressRef.current.active && (performance.now() - pipPressRef.current.at) <= PIP_PRESS_GUARD_MS) {
      console.log('[통화 화면] 포커스 무시 — 작은 창 누름 직후');
      return;
    }

    if (!isControlsVisibleRef.current) {
      console.log('[통화 화면] 버튼 표시 — 계기: 조작');
      setIsControlsVisible(true);
    }
    startHideTimer('자동 4초');
  }, [isMinimized, canHideControls, startHideTimer]);

  const controlsHideClass = videoMode && (!isControlsVisible || isMinimized) ? 'opacity-0 pointer-events-none' : 'opacity-100';
  const controlsTransitionClass = 'motion-safe:transition-opacity motion-safe:duration-200';

  // [B-2 단위 3-1b] 버튼 줄 높이를 통화 모달 최상위 요소의 --pip-bottom-gap 변수로 연동
  useEffect(() => {
    const el = controlsRef.current;
    const root = modalRootRef.current;
    if (!el || !root) return;

    const updateHeight = (h) => {
      if (root && h > 0) {
        root.style.setProperty('--pip-bottom-gap', `${Math.round(h)}px`);
      }
    };

    const rect = el.getBoundingClientRect();
    if (rect.height > 0) updateHeight(rect.height);

    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver((entries) => {
        for (const entry of entries) {
          const h = entry.borderBoxSize?.[0]?.blockSize ?? entry.contentRect?.height ?? 0;
          updateHeight(h);
        }
      });
      observer.observe(el);
      return () => {
        observer.disconnect();
      };
    }
  }, [callState, isVideoEnabled]);

  // 모달 최상위(z-[110]) 토스트 출력 헬퍼 (연속 클릭 시 타이머 리셋 및 자동 종료 보장)
  const showModalToast = useCallback((msg, duration = 3000) => {
    if (toastTimerRef.current) {
      clearTimeout(toastTimerRef.current);
      toastTimerRef.current = null;
    }
    setModalToastDuration(duration);
    setModalToastKey((prev) => prev + 1);
    setModalToast(msg);

    toastTimerRef.current = setTimeout(() => {
      setModalToast(null);
      toastTimerRef.current = null;
    }, duration);
  }, []);

  // [B-2 단위 2·3] 폰 전면↔후면 카메라 전환 버튼 노출 조건 및 연타 방지
  const [isSwitching, setIsSwitching] = useState(false);
  const switchingRef = useRef(false);
  const showSwitchButton = Boolean(
    isVideoEnabled && cameraOn && (isAndroid() || isIOS()) && cameraDeviceCount >= 2
  );

  const handleSwitchCameraClick = async () => {
    if (switchingRef.current || isSwitching || !switchCameraFacing) return;
    switchingRef.current = true;
    setIsSwitching(true);
    try {
      const res = await switchCameraFacing();
      if (res) {
        if (res.reason === 'failed_restored') {
          showModalToast(t('call.camera_switch_failed'), 8000);
        } else if (res.reason === 'failed_off') {
          showModalToast(t('call.camera_switch_lost'), 8000);
        }
      }
    } finally {
      switchingRef.current = false;
      setIsSwitching(false);
    }
  };

  // Body 스크롤 잠금 (작게 보기 상태에서는 뒤의 채팅 스크롤을 위해 해제)
  useEffect(() => {
    if (!isVisible || isMinimized) return;
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [isVisible, isMinimized]);

  // 통화 상태가 idle로 돌아가거나 언마운트 시 타이머 및 상태 초기화 (메모리 누수 방지)
  useEffect(() => {
    if (callState === 'idle') {
      setIsSpeakerToggled(false);
      setIsGuideOpen(false);
      setModalToast(null);
      lastNoticeIdRef.current = 0;
      lastCameraNoticeIdRef.current = 0;
      if (toastTimerRef.current) {
        clearTimeout(toastTimerRef.current);
        toastTimerRef.current = null;
      }
    }
    return () => {
      if (toastTimerRef.current) {
        clearTimeout(toastTimerRef.current);
        toastTimerRef.current = null;
      }
    };
  }, [callState]);

  const isConnected = callState === 'connected';

  /**
   * 소리 칸 역할 판정 훅 (1:1 통화 및 단체 통화 일원화)
   * - 'speaker': 스피커 전환 지원 (데스크톱 엣지/크롬)
   * - 'reconnect': 오디오 장치 재연결 지원 (모바일 PWA / 이어폰 연결 버튼)
   */
  const { outputRole, isSpeakerSupported } = useAudioOutputSupport(isVisible, isConnected);
  const [isReconnectingAudio, setIsReconnectingAudio] = useState(false);

  // 자동 마이크 변경 안내 (B-1a)
  useEffect(() => {
    if (!micChangeNotice || !isConnected) return;
    if (micChangeNotice.id && micChangeNotice.id === lastNoticeIdRef.current) return;
    if (micChangeNotice.id) {
      lastNoticeIdRef.current = micChangeNotice.id;
    }
    const label = (micChangeNotice.label || '').slice(0, 40);
    showModalToast(label ? t('call.mic_changed_named', { name: label }) : t('call.mic_changed'), 3000);
  }, [micChangeNotice, isConnected, showModalToast, t]);

  // 카메라 화면 안 나옴 안내 (B-2 단위 1-A)
  useEffect(() => {
    if (!cameraNotice || !isConnected) return;
    if (cameraNotice.id && cameraNotice.id === lastCameraNoticeIdRef.current) return;
    if (cameraNotice.id) {
      lastCameraNoticeIdRef.current = cameraNotice.id;
    }
    if (cameraNotice.type === 'switched') {
      const rawName = (cameraNotice.name || '').trim();
      const displayName = rawName ? stripCameraDeviceId(rawName).slice(0, 40) : t('call.camera_another');
      showModalToast(t('call.camera_switched', { name: displayName }), 10000);
    } else if (cameraNotice.type === 'no_picture') {
      const isDesktop = isWindows() || isMac();
      const browser = getBrowserName();
      let key = 'call.camera_no_picture';
      if (isDesktop && browser === 'chrome') {
        key = 'call.camera_no_picture_chrome';
      } else if (isDesktop && browser === 'edge') {
        key = 'call.camera_no_picture_edge';
      }
      showModalToast(t(key), 10000);
    }
  }, [cameraNotice, isConnected, showModalToast, t]);

  if (!isVisible) return null;

  // 통화 상대 표시 정보 결정 (incoming / calling / connected 상태와 무관하게 동일한 보존 정보 사용)
  const isIncoming = callState === 'incoming';
  const isCalling = callState === 'calling';
  const isPreparing = callState === 'preparing';
  const isAcceptDisabled = isIncoming && !hasRecoveredOfferSdp;

  const defaultUnknownName = t('chat_conversation.header_unknown_user');
  const targetUser = callerInfo || partner;
  const targetName = (targetUser?.name || targetUser?.nickname || '').trim() || defaultUnknownName;
  const targetUserId = targetUser?.id;
  const targetAvatar = targetUser?.avatar || null;

  // 경과 시간 포맷팅 (MM:SS)
  const formatTime = (totalSec) => {
    const mins = Math.floor(totalSec / 60);
    const secs = totalSec % 60;
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  /**
   * 스피커 장치 토글 핸들러
   */
  const handleSpeakerClick = async () => {
    if (outputRole !== 'speaker' || !isSpeakerSupported) {
      return;
    }

    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const audioOutputs = devices.filter((d) => d.kind === 'audiooutput');

      if (audioOutputs.length < 2) {
        return;
      }

      if (remoteAudioRef?.current && typeof remoteAudioRef.current.setSinkId === 'function') {
        const currentSinkId = remoteAudioRef.current.sinkId || '';
        const nextDevice = audioOutputs.find((d) => d.deviceId !== currentSinkId) || audioOutputs[0];
        await callAudioGraph.setOutputDevice(remoteAudioRef.current, nextDevice.deviceId);
        setIsSpeakerToggled((prev) => !prev);

        const successMsg = nextDevice.label
          ? t('call.speaker_switched_named', { device: nextDevice.label })
          : t('call.speaker_switched');
        showModalToast(successMsg, 3000);
      }
    } catch (err) {
      console.warn('[VoiceCallModal] setSinkId failed:', err);
      showModalToast(t('call.speaker_switch_failed'), 3000);
    }
  };

  const handlePlaceholderFeatureClick = () => {
    showModalToast(t('call.feature_in_verification'), 2000);
  };

  /**
   * [B-2 단위 1] 개발 모드 전용 3번 카메라 버튼 핸들러
   */
  const handleCameraDevClick = async () => {
    if (cameraOn) {
      await onStopCamera?.();
    } else {
      const res = await onStartCamera?.();
      if (res && !res.ok) {
        if (res.reason === 'not_ready') {
          showModalToast(t('call.camera_not_ready'), 2000);
        } else if (res.reason === 'permission_denied') {
          const isDesktop = isWindows() || isMac();
          showModalToast(t(isDesktop ? 'call.camera_permission_denied_desktop' : 'call.camera_permission_denied'), 3000);
        } else if (res.reason === 'unavailable') {
          showModalToast(t('call.camera_unavailable'), 3000);
        } else if (res.reason === 'peer_unsupported') {
          showModalToast(t('call.peer_video_unsupported'), 8000);
        }
      }
    }
  };

  /**
   * 오디오 장치(블루투스/이어폰) 재연결 핸들러
   */
  const handleReconnectAudioClick = async () => {
    if (isReconnectingAudio) return;
    setIsReconnectingAudio(true);
    try {
      if (typeof onReconnectAudio === 'function') {
        const success = await onReconnectAudio();
        if (success) {
          const label = String((await getCurrentMicLabel?.()) || '').slice(0, 40);
          showModalToast(label ? t('call.mic_reconnected_named', { name: label }) : t('call.device_reconnected'), 3000);
        } else {
          showModalToast(t('call.device_reconnect_failed'), 3000);
        }
      } else {
        showModalToast(t('call.device_reconnect_failed'), 3000);
      }
    } catch (e) {
      console.error('[VoiceCallModal] handleReconnectAudioClick error:', e);
      showModalToast(t('call.device_reconnect_failed'), 3000);
    } finally {
      setIsReconnectingAudio(false);
    }
  };

  return (
    <div
      ref={freePipRef}
      className={
        isMinimized
          ? userPos
            ? "fixed w-[120px] h-[160px] z-[100] rounded-2xl shadow-2xl overflow-hidden bg-black select-none pointer-events-auto border border-white/20 cursor-pointer animate-fadeIn touch-none"
            : "fixed top-[calc(env(safe-area-inset-top,0px)+64px)] right-[max(12px,calc((100vw-480px)/2+12px))] w-[120px] h-[160px] z-[100] rounded-2xl shadow-2xl overflow-hidden bg-black select-none pointer-events-auto border border-white/20 cursor-pointer animate-fadeIn touch-none"
          : videoMode
          ? "fixed inset-0 z-[100] flex items-center justify-center bg-black overflow-hidden select-none"
          : "fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm transition-opacity duration-200 select-none"
      }
      style={
        isMinimized && userPos && clampedPos
          ? {
              left: `${clampedPos.x}px`,
              top: `${clampedPos.y}px`,
              touchAction: 'none',
            }
          : isMinimized
          ? { touchAction: 'none' }
          : { WebkitTouchCallout: 'none' }
      }
      onContextMenu={!isMinimized ? (e) => e.preventDefault() : undefined}
      role="dialog"
      aria-modal={!isMinimized}
      aria-label={isMinimized ? t('call.expand', '크게 보기') : undefined}
      {...(isMinimized ? freeHandlers : {})}
    >
      {/* safe-area 측정용 숨김 요소 (JS에서 env() 픽셀값 직접 측정) */}
      <div
        ref={safeAreaRef}
        aria-hidden="true"
        className="fixed top-0 left-0 w-0 h-0 pt-[env(safe-area-inset-top,0px)] pb-[env(safe-area-inset-bottom,0px)] pointer-events-none invisible"
      />
      {/* [B-3b 보완 (나) 커밋 2] 큰 화면 얼굴 창 전용 안전 여백 측정 숨은 요소 */}
      <div
        ref={pipSafeAreaRef}
        aria-hidden="true"
        className="fixed top-0 left-0 w-0 h-0 pt-[env(safe-area-inset-top,0px)] pb-[env(safe-area-inset-bottom,0px)] pl-[env(safe-area-inset-left,0px)] pr-[env(safe-area-inset-right,0px)] pointer-events-none invisible"
      />
      {/* 모달 전용 최상위 토스트 (z-[110]) - 작게 보기 중에는 숨김 */}
      {!isMinimized && (
        <Toast
          key={modalToastKey}
          message={modalToast}
          duration={modalToastDuration}
          position="top"
          onClose={() => setModalToast(null)}
          onClick={() => {
            if (toastTimerRef.current) {
              clearTimeout(toastTimerRef.current);
              toastTimerRef.current = null;
            }
            setModalToast(null);
          }}
          className="fixed top-[calc(env(safe-area-inset-top,0px)+30px)] left-1/2 -translate-x-1/2 z-[110]"
        />
      )}

      {/* [C-9] 소리 자동재생 차단 시 눌러서 소리 켜기 안내 알약 버튼 */}
      {!isMinimized && isAudioBlocked && (
        <div className="fixed top-[calc(env(safe-area-inset-top,0px)+72px)] left-1/2 -translate-x-1/2 z-[105] animate-fadeIn pointer-events-auto">
          <button
            type="button"
            onClick={handleUnblockAudio}
            className="px-4 py-2 rounded-full bg-[#006565] text-white text-[13px] font-semibold shadow-xl border border-white/30 flex items-center gap-1.5 hover:bg-[#005050] active:scale-95 transition-all cursor-pointer"
          >
            <span>🔊</span>
            <span>{t('call.tap_to_unmute', '눌러서 소리 켜기')}</span>
          </button>
        </div>
      )}

      {/* 상대방 음성 스트림 수신용 숨김 오디오 엘리먼트 (항상 유지) */}
      <audio ref={remoteAudioRef} autoPlay playsInline className="hidden" />

      <div
        ref={modalRootRef}
        onContextMenu={!isMinimized ? (e) => e.preventDefault() : undefined}
        className={
          isMinimized
            ? "relative w-full h-full bg-[#111] overflow-hidden select-none"
            : videoMode
            ? "relative w-full h-full bg-[#111] flex flex-col justify-between items-center text-center overflow-hidden pt-[calc(env(safe-area-inset-top,0px)+16px)] pb-[calc(env(safe-area-inset-bottom,0px)+24px)] px-4 select-none"
            : "relative w-full max-w-[340px] bg-[#fbf9f1] rounded-3xl border-2 border-[#006565] shadow-2xl p-6 flex flex-col items-center text-center animate-scaleIn overflow-hidden select-none"
        }
        onClick={handleModalClick}
        onPointerDownCapture={handleModalPointerDownCapture}
        onPointerMove={handleModalPointerMove}
        onFocusCapture={handleModalFocusCapture}
      >
        {/* [B-2 단위 3] 영상 층: 시험 모드/개발 모드 전용 (운영에서는 DOM 미존재) */}
        {isVideoEnabled && (
          <div ref={stageRef} className="absolute inset-0 z-0 overflow-hidden pointer-events-none">
            {/* 상대 영상 타일 (spotlight에 따라 큰 화면 또는 작은 창) */}
            <div
              ref={(el) => {
                remoteTileRef.current = el;
                if (isRemotePip) pipRef.current = el;
              }}
              {...(isRemotePip && !isMinimized ? pipHandlers : {})}
              style={
                isRemotePip
                  ? {
                      touchAction: 'none',
                      userSelect: 'none',
                      ...(pipPos ? { left: `${pipPos.left}px`, top: `${pipPos.top}px` } : {}),
                    }
                  : undefined
              }
              className={`${
                isRemotePip
                  ? `absolute ${LOCAL_PIP_CLASS} ${pipPos ? '' : PIP_CORNER_CLASSES.tr} overflow-hidden bg-black/40 backdrop-blur-sm pointer-events-auto z-20`
                  : 'absolute inset-0 w-full h-full rounded-none border-0 shadow-none overflow-hidden bg-black/40 backdrop-blur-sm pointer-events-auto z-0'
              } ${
                hasRemoteVideo && (!isRemotePip || !isMinimized) ? 'opacity-100 scale-100' : 'opacity-0 scale-95 pointer-events-none'
              }`}
            >
              <VideoTile
                stream={remoteVideoStream}
                mirrored={false}
                className="w-full h-full"
              />
            </div>

            {/* 내 영상 창 (spotlight에 따라 큰 화면 또는 작은 창) */}
            <div
              ref={(el) => {
                localTileRef.current = el;
                if (isLocalPip) pipRef.current = el;
              }}
              {...(isLocalPip && !isMinimized ? pipHandlers : {})}
              style={
                isLocalPip
                  ? {
                      touchAction: 'none',
                      userSelect: 'none',
                      ...(pipPos ? { left: `${pipPos.left}px`, top: `${pipPos.top}px` } : {}),
                    }
                  : undefined
              }
              className={`${
                isLocalPip
                  ? `absolute ${LOCAL_PIP_CLASS} ${pipPos ? '' : PIP_CORNER_CLASSES.tr} overflow-hidden bg-black/40 backdrop-blur-sm pointer-events-auto z-20`
                  : 'absolute inset-0 w-full h-full rounded-none border-0 shadow-none overflow-hidden bg-black/40 backdrop-blur-sm transition-all duration-300 pointer-events-auto z-0'
              } ${
                hasLocalVideo && (!isLocalPip || !isMinimized) ? 'opacity-100 scale-100' : 'opacity-0 scale-95 pointer-events-none'
              }`}
            >
              <VideoTile
                stream={localVideoStream}
                mirrored={cameraFacing !== 'environment'}
                className="w-full h-full"
              />

              {/* [B-2 단위 3] 작은 창(얼굴 창) 안 카메라 전면↔후면 전환 버튼 */}
              {!isMinimized && showSwitchButton && isLocalPip && (
                <button
                  type="button"
                  onClick={handleSwitchCameraClick}
                  onPointerDown={(e) => e.stopPropagation()}
                  disabled={isSwitching}
                  className={`absolute bottom-1.5 right-1.5 w-12 h-12 rounded-full flex items-center justify-center bg-black/50 hover:bg-black/70 text-white shadow-lg active:scale-90 transition-all touch-manipulation cursor-pointer border border-white/30 z-30 ${
                    isSwitching ? 'cursor-not-allowed' : ''
                  } ${controlsHideClass} ${controlsTransitionClass}`}
                  title={t('call.camera_switch_facing')}
                  aria-label={t('call.camera_switch_facing')}
                >
                  <span className={`material-symbols-outlined text-[24px] transition-opacity ${isSwitching ? 'opacity-50' : ''}`}>cameraswitch</span>
                </button>
              )}
            </div>
          </div>
        )}

        {/* 영상 모드용 상·하단 어두운 그라데이션 띠 (전체 영상 표시 시 글자·버튼 시인성 확보) */}
        {!isMinimized && videoMode && (hasRemoteVideo || hasLocalVideo) && (
          <>
            <div className={`absolute top-0 left-0 right-0 h-36 bg-gradient-to-b from-black/80 via-black/40 to-transparent pointer-events-none z-10 ${controlsHideClass} ${controlsTransitionClass}`} />
            <div className={`absolute bottom-0 left-0 right-0 h-56 bg-gradient-to-t from-black/90 via-black/60 to-transparent pointer-events-none z-10 ${controlsHideClass} ${controlsTransitionClass}`} />
          </>
        )}
        {/* 하드웨어/OS 마이크 잠김 경고 띠 (통화 중이며 isMicLocked 일 때 상단 오버레이 표시) */}
        {isConnected && isMicLocked && !isGuideOpen && (
          <div className="absolute top-2 left-2 right-2 bg-[#ba1a1a] text-white rounded-2xl px-3 py-2 shadow-lg flex items-center justify-between gap-2 z-20 animate-fadeIn text-left border border-white/20">
            <div className="flex items-center gap-1.5 min-w-0 flex-1">
              <span className="material-symbols-outlined text-[17px] flex-shrink-0 text-white animate-pulse">
                mic_off
              </span>
              <span className="text-[11px] font-medium leading-snug text-white line-clamp-2">
                {t('call.mic_locked_banner', '마이크가 잠겨 있습니다. 키를 눌러(Fn+F8 등) 해제해 주세요')}
              </span>
            </div>
            <button
              type="button"
              onClick={() => setIsGuideOpen(true)}
              className="flex-shrink-0 text-[10.5px] font-bold bg-white/25 hover:bg-white/35 text-white px-2.5 py-1 rounded-full active:scale-95 transition-all whitespace-nowrap cursor-pointer"
            >
              {t('call.mic_locked_help_btn', '해결 방법')}
            </button>
          </div>
        )}

        {/* [해결 방법] 인라인 상세 안내 오버레이 (모달 내부) */}
        {isGuideOpen && (
          <div
            className="absolute inset-0 bg-[#fbf9f1] rounded-3xl p-5 flex flex-col justify-between z-30 text-left animate-fadeIn shadow-2xl border-2 border-[#ba1a1a]"
            onClick={(e) => e.stopPropagation()}
          >
            <div>
              <div className="flex items-center justify-between mb-3 border-b border-[#ba1a1a]/20 pb-2">
                <div className="flex items-center gap-1.5 text-[#ba1a1a] font-bold text-[14px]">
                  <span className="material-symbols-outlined text-[19px]">mic_off</span>
                  <span>{t('call.mic_guide_title', '마이크 잠김 해결 방법')}</span>
                </div>
                <button
                  type="button"
                  onClick={() => setIsGuideOpen(false)}
                  className="w-7 h-7 rounded-full flex items-center justify-center text-on-surface-variant hover:bg-black/5 active:scale-90 transition-all cursor-pointer"
                  aria-label={t('call.mic_guide_close_aria', '닫기')}
                >
                  <span className="material-symbols-outlined text-[18px]">close</span>
                </button>
              </div>

              <div className="text-[12px] text-[#1b1c17] space-y-2.5 leading-snug pr-0.5">
                <div className="flex items-start gap-1.5">
                  <span className="font-bold text-[#ba1a1a] flex-shrink-0">1.</span>
                  <span>{t('call.mic_guide_step1', '키보드의 마이크 모양 키를 눌러 주세요 (Fn+F8, Fn+F4 등 기종마다 다릅니다)')}</span>
                </div>
                <div className="flex items-start gap-1.5">
                  <span className="font-bold text-[#ba1a1a] flex-shrink-0">2.</span>
                  <span>{t('call.mic_guide_step2', '화면 우측 하단 스피커 아이콘 우클릭 → 소리 설정 → 입력 → 마이크 음소거 해제')}</span>
                </div>
                <div className="flex items-start gap-1.5">
                  <span className="font-bold text-[#ba1a1a] flex-shrink-0">3.</span>
                  <span>{t('call.mic_guide_step3', "주소창 왼쪽 자물쇠 아이콘 → 마이크 권한이 '허용'인지 확인")}</span>
                </div>
                <div className="flex items-start gap-1.5">
                  <span className="font-bold text-[#ba1a1a] flex-shrink-0">4.</span>
                  <span>{t('call.mic_guide_step4', '그래도 안 되면 통화를 종료하고 다시 연결해 주세요')}</span>
                </div>
              </div>
            </div>

            {/* 하단 버튼 영역: 닫기 & 통화 종료(종료 버튼 가려짐 방지) */}
            <div className="mt-4 flex items-center gap-2 pt-2 border-t border-outline-variant/30">
              <button
                type="button"
                onClick={() => setIsGuideOpen(false)}
                className="flex-1 py-2 rounded-full bg-[#006565] text-white font-semibold text-[13px] hover:bg-[#005050] active:scale-95 transition-all cursor-pointer text-center shadow-md"
              >
                {t('call.mic_guide_close_btn', '닫기')}
              </button>
              <button
                type="button"
                onClick={onEnd}
                className="py-2 px-3.5 rounded-full bg-[#ba1a1a] text-white font-semibold text-[13px] hover:bg-[#93000a] active:scale-95 transition-all cursor-pointer text-center shadow-md flex items-center justify-center gap-1"
                title={t('call.end')}
                aria-label={t('call.end')}
              >
                <span className="material-symbols-outlined text-[16px]">call_end</span>
                <span>{t('call.end')}</span>
              </button>
            </div>
          </div>
        )}

        {/* 상대방 아바타 */}
        <div
          className={`relative rounded-full border-2 shadow-md flex items-center justify-center overflow-hidden bg-surface-container flex-shrink-0 transition-all ${
            videoMode
              ? 'hidden'
              : 'w-24 h-24 border-[#006565]/20 mb-4'
          }`}
        >
          <Avatar
            userId={targetUserId}
            name={targetName}
            avatarUrl={targetAvatar}
            avatarSelected={true}
            size="w-24 h-24"
            className="w-full h-full object-cover"
          />
        </div>

        {/* [B-3b 다듬기] 큰 화면용 카메라 전환 버튼 — 그라데이션 띠·얼굴 창 위로(z-30), 작게 보기 왼쪽 */}
        {!isMinimized && showSwitchButton && !isLocalPip && hasLocalVideo && (
          <button
            type="button"
            onClick={handleSwitchCameraClick}
            onPointerDown={(e) => e.stopPropagation()}
            disabled={isSwitching}
            className={`absolute top-[calc(env(safe-area-inset-top,0px)+16px)] ${!showMinimizeButton ? (showRotateButton ? 'right-[calc(env(safe-area-inset-right,0px)+70px)]' : 'right-[calc(env(safe-area-inset-right,0px)+16px)]') : (showRotateButton ? 'right-[calc(env(safe-area-inset-right,0px)+124px)]' : 'right-[calc(env(safe-area-inset-right,0px)+70px)]')} w-[42px] h-[42px] rounded-full flex items-center justify-center bg-black/50 hover:bg-black/70 text-white border border-white/30 backdrop-blur-sm shadow-md active:scale-95 transition-all touch-manipulation cursor-pointer z-30 ${isSwitching ? 'cursor-not-allowed' : ''} ${controlsHideClass} ${controlsTransitionClass}`}
            title={t('call.camera_switch_facing')}
            aria-label={t('call.camera_switch_facing')}
          >
            <span className={`material-symbols-outlined text-[22px] transition-opacity ${isSwitching ? 'opacity-50' : ''}`}>cameraswitch</span>
          </button>
        )}

        {/* [B-3c 2차] 통화 큰 화면 화면 회전 버튼 (전체 화면 + 회전 허용) */}
        {!isMinimized && showRotateButton && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleRotateClick();
            }}
            onPointerDown={(e) => e.stopPropagation()}
            className={`absolute top-[calc(env(safe-area-inset-top,0px)+16px)] ${!showMinimizeButton ? 'right-[calc(env(safe-area-inset-right,0px)+16px)]' : 'right-[calc(env(safe-area-inset-right,0px)+70px)]'} w-[42px] h-[42px] rounded-full flex items-center justify-center ${
              isRotateOn
                ? 'bg-white/30 hover:bg-white/40 text-white border-white/70'
                : 'bg-black/50 hover:bg-black/70 text-white border-white/30'
            } border backdrop-blur-sm shadow-md active:scale-95 transition-all touch-manipulation cursor-pointer z-30 ${controlsHideClass} ${controlsTransitionClass}`}
            title={t('call.rotate_screen', '화면 회전')}
            aria-label={t('call.rotate_screen', '화면 회전')}
          >
            <span className="material-symbols-outlined text-[22px]">screen_rotation</span>
          </button>
        )}

        {/* [B-3b 단위 1] 작게 보기 버튼 (화면 오른쪽 위) */}
        {showMinimizeButton && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              console.info('[작게 보기] 줄임 — 계기: 버튼');
              if (!isRootScreen) {
                removeGuard('버튼');
              }
              setIsMinimized(true);
            }}
            onPointerDown={(e) => e.stopPropagation()}
            className={`absolute top-[calc(env(safe-area-inset-top,0px)+16px)] right-[calc(env(safe-area-inset-right,0px)+16px)] w-[42px] h-[42px] rounded-full flex items-center justify-center bg-black/50 hover:bg-black/70 text-white border border-white/30 backdrop-blur-sm shadow-md active:scale-95 transition-all touch-manipulation cursor-pointer z-30 ${controlsHideClass} ${controlsTransitionClass}`}
            title={t('call.minimize', '작게 보기')}
            aria-label={t('call.minimize', '작게 보기')}
          >
            <span className="material-symbols-outlined text-[22px]">close_fullscreen</span>
          </button>
        )}

        {/* 상대방 이름 */}
        <h3
          className={`font-bold truncate transition-colors ${
            videoMode
              ? `absolute top-[calc(env(safe-area-inset-top,0px)+12px)] left-4 text-left ${showRotateButton ? 'max-w-[calc(100%-194px)]' : 'max-w-[calc(100%-140px)]'} text-[15px] text-white drop-shadow-md z-20 ${controlsHideClass} ${controlsTransitionClass}`
              : 'font-headline-sm max-w-full px-2 text-[19px] text-on-surface mb-1'
          }`}
        >
          {targetName}
        </h3>

        {/* 상대방 음소거 안내 문구 (통화 중이며 상대가 음소거한 경우에만 노출) */}
        {isConnected && peerMuted && (
          <div
            className={`flex items-center gap-1 font-medium animate-fadeIn ${
              videoMode
                ? `absolute top-[calc(env(safe-area-inset-top,0px)+62px)] left-4 text-left text-white/90 drop-shadow-md z-20 text-[11px] max-w-[calc(100%-140px)] truncate ${controlsHideClass} ${controlsTransitionClass}`
                : 'justify-center text-[12px] mb-1.5 text-[#ba1a1a]'
            }`}
          >
            <span className="material-symbols-outlined text-[15px]">mic_off</span>
            <span>{t('call.peer_muted')}</span>
          </div>
        )}

        {/* 통화 상태 문구 / 타이머 */}
        <div
          className={`flex items-center transition-all ${
            videoMode
              ? `absolute top-[calc(env(safe-area-inset-top,0px)+36px)] left-4 justify-start z-20 ${controlsHideClass} ${controlsTransitionClass}`
              : 'min-h-[28px] mb-6 justify-center'
          }`}
        >
          {isPreparing && (
            <div
              className={`flex items-center gap-1.5 font-medium animate-pulse ${
                videoMode ? 'text-white drop-shadow-md text-[13px]' : 'text-[#006565] text-[14px]'
              }`}
            >
              <span className="material-symbols-outlined text-[18px]">hourglass_top</span>
              <span>{t('call.preparing_connection', '연결 준비 중')}…</span>
            </div>
          )}

          {isCalling && (
            <div
              className={`flex items-center gap-1.5 font-medium animate-pulse ${
                videoMode ? 'text-white drop-shadow-md text-[13px]' : 'text-[#006565] text-[14px]'
              }`}
            >
              <span className="material-symbols-outlined text-[18px]">ring_volume</span>
              <span>{t('call.calling')}</span>
            </div>
          )}

          {isIncoming && (
            <div
              className={`flex items-center gap-1.5 font-semibold ${
                videoMode ? 'text-white drop-shadow-md text-[13px]' : 'text-[#006565] text-[14px]'
              }`}
            >
              <span className="material-symbols-outlined text-[20px] animate-bounce">
                {isVideoEnabled && incomingKind === 'video' ? 'videocam' : 'phone_in_talk'}
              </span>
              <span>
                {isVideoEnabled && incomingKind === 'video' ? t('call.incoming_video') : t('call.incoming')}
              </span>
            </div>
          )}

          {isConnected && (
            <div className="flex flex-col items-center gap-2">
              <div
                className={`flex items-center gap-1.5 font-mono font-bold rounded-full transition-colors ${
                  videoMode
                    ? 'bg-black/40 text-white border border-white/20 backdrop-blur-sm shadow-sm text-[12px] px-2.5 py-0.5'
                    : 'bg-[#006565]/10 text-[#006565] text-[14px] px-3.5 py-0.5'
                }`}
              >
                <span
                  className={`w-2 h-2 rounded-full animate-ping ${
                    videoMode ? 'bg-white' : 'bg-[#006565]'
                  }`}
                />
                <span>{isAudioConnected ? formatTime(elapsedSeconds) : t('call.calling', '연결 중...')}</span>
              </div>

              {(isNetworkReconnectingSelf || isNetworkReconnectingPeer || peerRecoveryRemainingSec !== null) && (
                <div
                  className={`flex items-center gap-1.5 font-medium rounded-full transition-colors animate-pulse text-center ${
                    videoMode
                      ? 'bg-black/60 text-white border border-white/20 backdrop-blur-sm shadow-sm text-[12px] px-3 py-1 max-w-[320px]'
                      : 'bg-[#006565]/10 text-[#006565] text-[12px] px-3.5 py-1 max-w-[320px]'
                  }`}
                >
                  <span className="material-symbols-outlined text-[16px] animate-spin shrink-0">sync</span>
                  <span className="leading-tight">
                    {isNetworkReconnectingSelf
                      ? t('call.net_changed_self')
                      : t('call.net_changed_peer')}
                  </span>
                </div>
              )}

              {isPeerAudioUnstable && !isNetworkReconnectingPeer && peerRecoveryRemainingSec === null && !isNetworkReconnectingSelf && (
                <div
                  className={`flex items-center gap-1.5 font-medium rounded-full transition-colors animate-pulse text-center ${
                    videoMode
                      ? 'bg-black/60 text-white border border-white/20 backdrop-blur-sm shadow-sm text-[12px] px-3 py-1 max-w-[320px]'
                      : 'bg-[#006565]/10 text-[#006565] text-[12px] px-3.5 py-1 max-w-[320px]'
                  }`}
                >
                  <span className="material-symbols-outlined text-[16px] animate-spin shrink-0">sync</span>
                  <span className="leading-tight">{t('call.peer_unstable')}</span>
                </div>
              )}

              {isReconnectedSuccess && !isNetworkReconnectingSelf && !isNetworkReconnectingPeer && (
                <div
                  className={`flex items-center gap-1.5 font-medium rounded-full transition-colors text-center ${
                    videoMode
                      ? 'bg-emerald-950/60 text-emerald-300 border border-emerald-400/30 backdrop-blur-sm shadow-sm text-[12px] px-3 py-1'
                      : 'bg-emerald-50 text-emerald-700 text-[12px] px-3.5 py-1'
                  }`}
                >
                  <span className="material-symbols-outlined text-[16px] shrink-0">check_circle</span>
                  <span>{t('call.reconnected')}</span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* [B-2 단위 3-1c] 버튼이 숨은 상태이고 내가 음소거 중일 때 상단 중앙 음소거 표시 */}
        {!isMinimized && videoMode && !isControlsVisible && isMuted && (
          <div
            className="absolute top-[calc(env(safe-area-inset-top,0px)+16px)] left-1/2 -translate-x-1/2 z-20 pointer-events-none flex items-center gap-1.5 px-3 py-1 rounded-full bg-black/60 text-white/90 border border-white/20 backdrop-blur-sm text-[12px] font-medium shadow-sm animate-fadeIn"
            aria-live="polite"
          >
            <span className="material-symbols-outlined text-[15px] text-[#ff897d]">mic_off</span>
            <span>{t('call.muted_indicator', '음소거 중')}</span>
          </div>
        )}

        {/* 제어 버튼 그룹 */}
        {isIncoming ? (
          isVideoEnabled && incomingKind === 'video' ? (
            /* [B-3a 단위 1: 화상 수신 화면 - 거절 / 음성으로 받기 / 화상으로 받기 3버튼] */
            <div className={`flex items-center justify-center gap-6 w-full px-2 ${videoMode ? 'mt-auto z-20' : ''}`}>
              {/* 거절 버튼 */}
              <div className="flex flex-col items-center gap-1.5">
                <button
                  type="button"
                  onClick={onDecline || onEnd}
                  disabled={isAcceptDisabled || isAccepting}
                  className={`w-14 h-14 rounded-full bg-[#ba1a1a] text-white flex items-center justify-center shadow-lg transition-all ${
                    isAccepting ? 'opacity-50 cursor-not-allowed' : 'hover:bg-[#93000a] active:scale-95 cursor-pointer'
                  }`}
                  title={t('call.decline')}
                  aria-label={t('call.decline')}
                >
                  <span className="material-symbols-outlined text-[28px]">call_end</span>
                </button>
                <span className="text-[12px] text-on-surface-variant font-medium">{t('call.decline')}</span>
              </div>

              {/* 음성으로 받기 버튼 */}
              <div className="flex flex-col items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => onAccept?.({ withVideo: false })}
                  disabled={isAcceptDisabled || isAccepting}
                  className={`w-14 h-14 rounded-full text-white flex items-center justify-center shadow-lg transition-all ${
                    (isAcceptDisabled || isAccepting)
                      ? 'bg-[#006565]/40 text-white/50 cursor-not-allowed'
                      : 'bg-[#006565] hover:bg-[#005050] active:scale-95 cursor-pointer animate-pulse'
                  }`}
                  title={isAccepting ? t('call.calling', '연결 중...') : (isAcceptDisabled ? t('call.preparing_connection') : t('call.accept_voice'))}
                  aria-label={isAccepting ? t('call.calling', '연결 중...') : (isAcceptDisabled ? t('call.preparing_connection') : t('call.accept_voice'))}
                >
                  <span className={`material-symbols-outlined text-[28px] ${isAccepting ? 'animate-spin' : ''}`}>
                    {isAccepting ? 'progress_activity' : (isAcceptDisabled ? 'hourglass_top' : 'call')}
                  </span>
                </button>
                <span className="text-[12px] text-on-surface-variant font-medium text-center truncate max-w-[80px]">
                  {isAccepting ? t('call.calling', '연결 중...') : (isAcceptDisabled ? t('call.preparing_connection') : t('call.accept_voice'))}
                </span>
              </div>

              {/* 화상으로 받기 버튼 */}
              <div className="flex flex-col items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => onAccept?.({ withVideo: true })}
                  disabled={isAcceptDisabled || isAccepting}
                  className={`w-14 h-14 rounded-full text-white flex items-center justify-center shadow-lg transition-all ${
                    (isAcceptDisabled || isAccepting)
                      ? 'bg-[#006565]/40 text-white/50 cursor-not-allowed'
                      : 'bg-[#006565] hover:bg-[#005050] active:scale-95 cursor-pointer animate-pulse'
                  }`}
                  title={isAccepting ? t('call.calling', '연결 중...') : (isAcceptDisabled ? t('call.preparing_connection') : t('call.accept_video'))}
                  aria-label={isAccepting ? t('call.calling', '연결 중...') : (isAcceptDisabled ? t('call.preparing_connection') : t('call.accept_video'))}
                >
                  <span className={`material-symbols-outlined text-[28px] ${isAccepting ? 'animate-spin' : ''}`}>
                    {isAccepting ? 'progress_activity' : (isAcceptDisabled ? 'hourglass_top' : 'videocam')}
                  </span>
                </button>
                <span className="text-[12px] text-on-surface-variant font-medium text-center truncate max-w-[80px]">
                  {isAccepting ? t('call.calling', '연결 중...') : (isAcceptDisabled ? t('call.preparing_connection') : t('call.accept_video'))}
                </span>
              </div>
            </div>
          ) : (
            /* [수신 중 화면 - 기존 확정 디자인 100% 유지] */
            <div className={`flex items-center justify-center gap-10 w-full px-2 ${videoMode ? 'mt-auto z-20' : ''}`}>
              {/* 거절 버튼 */}
              <div className="flex flex-col items-center gap-1.5">
                <button
                  type="button"
                  onClick={onDecline || onEnd}
                  disabled={isAcceptDisabled || isAccepting}
                  className={`w-14 h-14 rounded-full bg-[#ba1a1a] text-white flex items-center justify-center shadow-lg transition-all ${
                    isAccepting ? 'opacity-50 cursor-not-allowed' : 'hover:bg-[#93000a] active:scale-95 cursor-pointer'
                  }`}
                  title={t('call.decline')}
                  aria-label={t('call.decline')}
                >
                  <span className="material-symbols-outlined text-[28px]">call_end</span>
                </button>
                <span className="text-[12px] text-on-surface-variant font-medium">{t('call.decline')}</span>
              </div>

              {/* 받기 버튼 */}
              <div className="flex flex-col items-center gap-1.5">
                <button
                  type="button"
                  onClick={onAccept}
                  disabled={isAcceptDisabled || isAccepting}
                  className={`w-14 h-14 rounded-full text-white flex items-center justify-center shadow-lg transition-all ${
                    (isAcceptDisabled || isAccepting)
                      ? 'bg-[#006565]/40 text-white/50 cursor-not-allowed'
                      : 'bg-[#006565] hover:bg-[#005050] active:scale-95 cursor-pointer animate-pulse'
                  }`}
                  title={isAccepting ? t('call.calling', '연결 중...') : (isAcceptDisabled ? t('call.preparing_connection') : t('call.accept'))}
                  aria-label={isAccepting ? t('call.calling', '연결 중...') : (isAcceptDisabled ? t('call.preparing_connection') : t('call.accept'))}
                >
                  <span className={`material-symbols-outlined text-[28px] ${isAccepting ? 'animate-spin' : ''}`}>
                    {isAccepting ? 'progress_activity' : (isAcceptDisabled ? 'hourglass_top' : 'call')}
                  </span>
                </button>
                <span className="text-[12px] text-on-surface-variant font-medium">
                  {isAccepting ? t('call.calling', '연결 중...') : (isAcceptDisabled ? t('call.preparing_connection') : t('call.accept'))}
                </span>
              </div>
            </div>
          )
        ) : (isCalling || isPreparing) ? (
          /* [발신 중 화면 - 기존 확정 디자인 100% 유지] */
          <div className={`flex flex-col items-center gap-1.5 ${videoMode ? 'mt-auto z-20' : ''}`}>
            <button
              type="button"
              onClick={onEnd}
              className="w-14 h-14 rounded-full bg-[#ba1a1a] text-white flex items-center justify-center shadow-lg hover:bg-[#93000a] active:scale-95 transition-all cursor-pointer"
              title={t('call.end')}
              aria-label={t('call.end')}
            >
              <span className="material-symbols-outlined text-[28px]">call_end</span>
            </button>
            <span className="text-[12px] text-on-surface-variant font-medium">{t('call.end')}</span>
          </div>
        ) : (
          /* [통화 중 화면 - 종료 버튼 + 구분선 + 기능 버튼 4개] */
          <div
            ref={controlsRef}
            className={`flex flex-col items-center w-full ${
              videoMode ? `mt-auto z-20 pb-[calc(env(safe-area-inset-bottom,0px)+12px)] ${controlsHideClass} ${controlsTransitionClass}` : ''
            }`}
          >
            {/* 종료 버튼 (오조작 방지를 위해 상단 배치) */}
            <div className="flex flex-col items-center gap-1.5">
              <button
                type="button"
                onClick={onEnd}
                className="w-14 h-14 rounded-full bg-[#ba1a1a] text-white flex items-center justify-center shadow-lg hover:bg-[#93000a] active:scale-95 transition-all cursor-pointer"
                title={t('call.end')}
                aria-label={t('call.end')}
              >
                <span className="material-symbols-outlined text-[28px]">call_end</span>
              </button>
              <span className={`text-[12px] font-medium ${videoMode ? 'text-white/90 drop-shadow-sm' : 'text-on-surface-variant'}`}>
                {t('call.end')}
              </span>
            </div>

            {/* 오조작 방지 얇은 구분선 및 여백 (최소 20px 이상) */}
            <div className={`w-full my-5 border-t ${videoMode ? 'border-white/20' : 'border-[#006565]/15'}`} />

            {/* 기능 버튼 (항상 4칸 고정: 음소거 · 소리 칸 · 화상 · 통역) */}
            <div className="flex items-center justify-between w-full px-1">
              {/* (1) 마이크 음소거 */}
              <div className="flex flex-col items-center gap-1.5 w-[56px]">
                <button
                  type="button"
                  onClick={onToggleMute}
                  className={`w-11 h-11 rounded-full flex flex-col items-center justify-center shadow-sm transition-all active:scale-95 cursor-pointer ${
                    isMuted
                      ? 'bg-[#ba1a1a] text-white hover:bg-[#93000a]'
                      : 'bg-surface-container text-on-surface hover:bg-surface-container-high border border-outline-variant/30'
                  }`}
                  title={t('call.mute')}
                  aria-label={t('call.mute')}
                >
                  <span className="material-symbols-outlined text-[19px] leading-none mb-0.5">
                    {isMuted ? 'mic_off' : 'mic'}
                  </span>
                  <span className="text-[9px] font-bold leading-none select-none">
                    {isMuted ? 'OFF' : 'ON'}
                  </span>
                </button>
                <span className={`text-[11px] font-medium ${videoMode ? 'text-white/90' : 'text-on-surface-variant'}`}>
                  {t('call.mute')}
                </span>
              </div>

              {/* (2) 소리 칸 (outputRole === 'speaker' : 스피커 전환 / outputRole === 'reconnect' : 이어폰 연결) */}
              {outputRole === 'speaker' ? (
                <div className="flex flex-col items-center gap-1.5 w-[56px]">
                  <button
                    type="button"
                    onClick={handleSpeakerClick}
                    className={`w-11 h-11 rounded-full flex items-center justify-center shadow-sm transition-all active:scale-95 cursor-pointer ${
                      isSpeakerToggled
                        ? 'bg-[#006565] text-white hover:bg-[#005050]'
                        : 'bg-surface-container text-on-surface hover:bg-surface-container-high border border-outline-variant/30'
                    }`}
                    title={t('call.speaker')}
                    aria-label={t('call.speaker')}
                  >
                    <span className="material-symbols-outlined text-[22px]">
                      volume_up
                    </span>
                  </button>
                  <span className={`text-[11px] font-medium ${videoMode ? 'text-white/90' : 'text-on-surface-variant'}`}>
                    {t('call.speaker')}
                  </span>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-1.5 w-[56px]">
                  <button
                    type="button"
                    onClick={handleReconnectAudioClick}
                    disabled={isReconnectingAudio}
                    className="w-11 h-11 rounded-full flex items-center justify-center shadow-sm transition-all active:scale-95 cursor-pointer bg-surface-container text-on-surface hover:bg-surface-container-high border border-outline-variant/30 disabled:opacity-50"
                    title={t('call.earphones')}
                    aria-label={t('call.earphones')}
                  >
                    <span className="material-symbols-outlined text-[22px]">
                      headphones
                    </span>
                  </button>
                  <span className={`text-[11px] font-medium text-center truncate max-w-[56px] ${videoMode ? 'text-white/90' : 'text-on-surface-variant'}`}>
                    {t('call.earphones')}
                  </span>
                </div>
              )}

              {/* (3) 화상 전환 */}
              {!effectivePeerVideoCapable ? (
                <div className="flex flex-col items-center gap-1.5 w-[56px]">
                  <button
                    type="button"
                    onClick={() => showModalToast(t('call.peer_video_unsupported'), 8000)}
                    className="w-11 h-11 rounded-full flex items-center justify-center shadow-sm transition-all active:scale-95 cursor-pointer bg-surface-container/50 text-on-surface-variant/40 border border-outline-variant/20 hover:bg-surface-container/50"
                    title={t('call.video')}
                    aria-label={t('call.video')}
                  >
                    <span className="material-symbols-outlined text-[22px]">videocam</span>
                  </button>
                  <span className="text-[11px] text-on-surface-variant/40 font-medium">
                    {t('call.video')}
                  </span>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-1.5 w-[56px]">
                  <button
                    type="button"
                    onClick={handleCameraDevClick}
                    className={`w-11 h-11 rounded-full flex flex-col items-center justify-center shadow-sm transition-all active:scale-95 cursor-pointer ${
                      cameraOn
                        ? 'bg-[#006565] text-white hover:bg-[#005050]'
                        : 'bg-surface-container text-on-surface hover:bg-surface-container-high border border-outline-variant/30'
                    }`}
                    title={t('call.video')}
                    aria-label={t('call.video')}
                  >
                    <span className="material-symbols-outlined text-[19px] leading-none mb-0.5">videocam</span>
                    <span className="text-[9px] font-bold leading-none select-none">
                      {cameraOn ? 'ON' : 'OFF'}
                    </span>
                  </button>
                  <span className={`text-[11px] font-medium ${videoMode ? 'text-white/90' : 'text-on-surface-variant'}`}>
                    {t('call.video')}
                  </span>
                </div>
              )}

              {/* (4) AI 실시간 통역 (기능 검증중) */}
              <div className="flex flex-col items-center gap-1.5 w-[56px]">
                <button
                  type="button"
                  onClick={handlePlaceholderFeatureClick}
                  className="w-11 h-11 rounded-full flex items-center justify-center shadow-sm transition-all active:scale-95 cursor-pointer bg-surface-container/50 text-on-surface-variant/40 border border-outline-variant/20 hover:bg-surface-container/50"
                  title={t('call.translate')}
                  aria-label={t('call.translate')}
                >
                  <span className="material-symbols-outlined text-[22px]">translate</span>
                </button>
                <span className={`text-[11px] font-medium ${videoMode ? 'text-white/90' : 'text-on-surface-variant'}`}>
                  {t('call.translate')}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


